// Jalali (Persian) calendar helpers for the date-range picker. Uses the
// platform's built-in Persian calendar (Intl, widely supported in modern
// browsers) instead of a hand-rolled conversion algorithm - avoids any risk
// of a custom algorithm disagreeing with the standard calendar by a day at
// leap-year edges. All calendar-grid math is done on plain Gregorian Date
// objects (well-understood day arithmetic); Jalali numbers are only read
// via Intl for display and for the day-of-month/month-boundary checks.
// numberingSystem: 'latn' is required here - the 'fa-IR' locale defaults to
// Persian-digit glyphs (۰۱۲۳...) for the formatted parts, and Number('۴')
// is NaN (Number() only understands ASCII digits). Without forcing Latin
// digits, jy/jm/jd all came back NaN, which made every month-boundary
// check (`info.jm !== jm`) trivially true (NaN !== NaN), breaking the
// month-building loop before it collected a single day and crashing on
// monthDays[-1].date.
const FA_FORMATTER = new Intl.DateTimeFormat('fa-IR-u-ca-persian', { year: 'numeric', month: 'numeric', day: 'numeric', numberingSystem: 'latn' });

export const JALALI_MONTHS = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
export const JALALI_WEEKDAYS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج']; // Saturday-first

export function toJalali(date) {
  const parts = FA_FORMATTER.formatToParts(date);
  const get = (t) => Number(parts.find((p) => p.type === t)?.value);
  return { jy: get('year'), jm: get('month'), jd: get('day') };
}

export const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
export const startOfDay = (date) => { const d = new Date(date); d.setHours(0, 0, 0, 0); return d; };

// Gregorian Date -> the Gregorian Date of the 1st of that same Jalali month.
export function firstOfJalaliMonth(date) {
  let d = startOfDay(date);
  const targetMonth = toJalali(d).jm;
  for (let i = 0; i < 32; i++) {
    const prev = addDays(d, -1);
    if (toJalali(prev).jm !== targetMonth) return d;
    d = prev;
  }
  return d;
}

// Builds a full Saturday-first calendar grid (weeks of 7 days, padded with
// adjacent-month days) for the Jalali month containing `anchor`.
export function buildJalaliMonthGrid(anchor) {
  const first = firstOfJalaliMonth(anchor);
  const { jy, jm } = toJalali(first);
  const monthDays = [];
  let d = first;
  for (let i = 0; i < 32; i++) {
    const info = toJalali(d);
    if (info.jm !== jm) break;
    monthDays.push({ date: d, ...info, inMonth: true });
    d = addDays(d, 1);
  }
  const leadCount = (first.getDay() + 1) % 7; // JS Sunday=0..Saturday=6 -> reindex to Saturday-first=0
  const lead = [];
  for (let i = leadCount; i > 0; i--) {
    const dd = addDays(first, -i);
    lead.push({ date: dd, ...toJalali(dd), inMonth: false });
  }
  const all = [...lead, ...monthDays];
  while (all.length % 7 !== 0) {
    const next = addDays(all[all.length - 1].date, 1);
    all.push({ date: next, ...toJalali(next), inMonth: false });
  }
  const weeks = [];
  for (let i = 0; i < all.length; i += 7) weeks.push(all.slice(i, i + 7));
  return { jy, jm, firstDate: first, lastDate: monthDays[monthDays.length - 1].date, weeks };
}

export const jalaliLabel = (jy, jm) => `${JALALI_MONTHS[jm - 1]} ${jy}`;

// {year, month(2digit), day(2digit)} matching what the backend's
// start_date[...]/end_date[...] params expect (Jalali) - used for
// endpoints that themselves convert to/compare against a Jalali value.
export function toBackendDateParams(date) {
  const { jy, jm, jd } = toJalali(date);
  const pad2 = (n) => String(n).padStart(2, '0');
  return { year: String(jy), month: pad2(jm), day: pad2(jd) };
}

// Same shape, but Gregorian - for endpoints (like getDistanceReport) that
// build a plain "YYYY-MM-DD ..." string compared directly against a real
// MySQL DATETIME column (populated via NOW(), i.e. genuinely Gregorian).
// The calendar UI itself is Jalali, but `date` is still a normal JS Date
// object underneath - its own Gregorian fields are exactly what those
// endpoints need, no Jalali conversion involved.
export function toGregorianDateParams(date) {
  const pad2 = (n) => String(n).padStart(2, '0');
  return { year: String(date.getFullYear()), month: pad2(date.getMonth() + 1), day: pad2(date.getDate()) };
}

export const isSameDay = (a, b) => !!a && !!b && a.toDateString() === b.toDateString();
export const isWithin = (d, a, b) => {
  if (!a || !b) return false;
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  return d >= lo && d <= hi;
};
